import { Flex, BoxProps, CloseButton, Button, Spacer } from "@chakra-ui/react";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  FieldState,
  FilterByFieldName,
  clearFilters,
  removeFilterField,
  applyFilters,
  discardDraft,
} from "../../store/filter";
import {
  DistanceFilter as DistanceFilterType,
  Filter as FilterType,
  MultipleFilter,
  FilterRule,
  Rules,
} from "../../types/UIConfig";
import { DistanceFilter } from "./components/DistanceFilter";
import { MultipleSelectFilter } from "./components/MultipleSelectFilter";
import { CheckEligibility } from "./components/CheckEligibility";
import { setShowCheckEligibilityModal } from "../../store/modals";
import { useTranslations } from "../../hooks/useTranslations";

type Props = {
  onClose: () => void;
} & BoxProps;

function getSelectedValues(
  filter: MultipleFilter,
  filterByFieldName: FilterByFieldName
): string[] {
  if (filter.fieldName in filterByFieldName) {
    const filterState =
      filterByFieldName[filter.fieldName] || ({} as FieldState);
    if (filterState.type === "multiple") return filterState.selected;
  }

  return [];
}

function getSelectedDistanceValue(
  filter: DistanceFilterType,
  filterByFieldName: FilterByFieldName
): number | undefined {
  if (filter.fieldName in filterByFieldName) {
    const filterState =
      filterByFieldName[filter.fieldName] || ({} as FieldState);
    if (filterState.type === "distance") return filterState.selected;
  }
}

export function getFilterStateByFieldName(
  fieldName: string,
  filterByFieldName: FilterByFieldName
): FieldState | undefined {
  return filterByFieldName[fieldName];
}

export function evaluateFilterRule(
  rule: FilterRule,
  filterByFieldName: FilterByFieldName
): boolean {
  const filterState = getFilterStateByFieldName(
    rule.property,
    filterByFieldName
  );

  if (!filterState) {
    return false;
  }

  if (rule.dataType === "multiselectFilter") {
    if (filterState.type !== "multiple") {
      return false;
    }

    const selectedValues = filterState.selected;
    const ruleOptions = rule.options;

    switch (rule.operator) {
      case "IN":
        return selectedValues.some((val) => ruleOptions.includes(val));
      case "NIN":
        return !selectedValues.some((val) => ruleOptions.includes(val));
      case "EQ":
        return (
          selectedValues.length === ruleOptions.length &&
          selectedValues.every((val: string) => ruleOptions.includes(val)) &&
          ruleOptions.every((val: string) => selectedValues.includes(val))
        );
      case "NEQ":
        return !(
          selectedValues.length === ruleOptions.length &&
          selectedValues.every((val: string) => ruleOptions.includes(val)) &&
          ruleOptions.every((val: string) => selectedValues.includes(val))
        );
      default:
        return false;
    }
  }

  if (rule.dataType === "distanceFilter") {
    if (filterState.type !== "distance") {
      return false;
    }

    const selectedValue = filterState.selected;
    const ruleValue = rule.option;

    switch (rule.operator) {
      case "LT":
        return selectedValue < ruleValue;
      case "GT":
        return selectedValue > ruleValue;
      case "LTE":
        return selectedValue <= ruleValue;
      case "GTE":
        return selectedValue >= ruleValue;
      case "EQ":
        return selectedValue === ruleValue;
      case "NEQ":
        return selectedValue !== ruleValue;
      default:
        return false;
    }
  }

  return false;
}

export function evaluateRules(
  rules: Rules,
  filterByFieldName: FilterByFieldName
): boolean {
  return rules.rules.every((rule: FilterRule) =>
    evaluateFilterRule(rule, filterByFieldName)
  );
}

export function shouldShowFilter(
  filter: FilterType,
  filterByFieldName: FilterByFieldName
): boolean {
  if (!filter.visibility) {
    return true;
  }

  const visibility = filter.visibility;
  let conditionMet = false;

  if (visibility.allOf) {
    conditionMet = visibility.allOf.every((rules) =>
      evaluateRules(rules, filterByFieldName)
    );
  } else if (visibility.anyOf) {
    conditionMet = visibility.anyOf.some((rules) =>
      evaluateRules(rules, filterByFieldName)
    );
  } else if (visibility.oneOf) {
    const matches = visibility.oneOf.filter((rules) =>
      evaluateRules(rules, filterByFieldName)
    );
    conditionMet = matches.length === 1;
  } else {
    return visibility.default === "VISIBLE";
  }

  return conditionMet
    ? visibility.result === "VISIBLE"
    : visibility.default === "VISIBLE";
}

export const Filter = ({
  onClose,
  ...props
}: Props): React.ReactElement | null => {
  const config = useSelector((state: RootState) => state.config);
  const filterSlice = useSelector((state: RootState) => state.filter);
  const address = useSelector((state: RootState) => state.address);
  const placements = useSelector((state: RootState) => state.placements);
  const dispatch = useDispatch();
  const translate = useTranslations();

  const [previousVisibility, setPreviousVisibility] = React.useState<
    Record<string, boolean>
  >({});

  React.useEffect(() => {
    if (config.state !== "loading" && placements.status !== "loading") {
      const currentVisibility: Record<string, boolean> = {};

      config.values.filters?.forEach((filter: FilterType) => {
        const isVisible = shouldShowFilter(filter, filterSlice.draft);
        currentVisibility[filter.fieldName] = isVisible;

        if (
          previousVisibility[filter.fieldName] === true &&
          isVisible === false
        ) {
          dispatch(removeFilterField({ fieldName: filter.fieldName }));
        }
      });

      setPreviousVisibility(currentVisibility);
    }
  }, [config, filterSlice.draft, placements, dispatch]);

  if (config.state === "loading" || placements.status === "loading")
    return null;

  const eligibilityButtonText = placements.eligibilityEnabled
    ? translate((t) => t.eligibility.welcomeModal.changeEligibility.label)
    : translate((t) => t.eligibility.welcomeModal.continueButton.label);

  const showEligibilityToggle =
    (config.values.eligibility?.enableShowHideEligible &&
      placements.eligibilityEnabled) ??
    false;

  React.useEffect(() => {
    return () => {
      dispatch(discardDraft());
    };
  }, []);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isResetting, setIsResetting] = React.useState(false);
  const [bodyRef, hasOverflow] = useHasOverflow<HTMLDivElement>();

  return (
    <Flex
      ref={bodyRef}
      {...props}
      width="100%"
      position="relative"
      overflow="auto"
      height="100%"
      direction="column"
      background="gray.50"
    >
      <Flex align="center" marginTop={2}>
        <Spacer />
        <CloseButton onClick={onClose} marginX={2} size="md" />
      </Flex>
      <Flex direction="column" gap={4} padding={6} zIndex={props.zIndex}>
        {config.values.eligibility?.isEnabled && (
          <CheckEligibility
            buttonText={eligibilityButtonText}
            onCheckEligibility={() => {
              dispatch(setShowCheckEligibilityModal(true));
              onClose();
            }}
            showToggle={showEligibilityToggle}
          />
        )}
        {config.values.filters?.map((filter: FilterType) => {
          if (!shouldShowFilter(filter, filterSlice.draft)) {
            return null;
          }

          switch (filter.type) {
            case "multiple":
              return (
                <MultipleSelectFilter
                  key={filter.fieldName}
                  filter={filter}
                  selectedValues={getSelectedValues(filter, filterSlice.draft)}
                />
              );
            case "distance":
              return (
                <DistanceFilter
                  key={filter.fieldName}
                  filter={filter}
                  selectedValue={getSelectedDistanceValue(
                    filter,
                    filterSlice.draft
                  )}
                  isDisabled={address.status !== "finished"}
                  disabledReason={
                    <FormattedMessage id="DISTANCE_DISABLED_REASON" />
                  }
                />
              );
            default:
              return <></>;
          }
        })}
      </Flex>
      <Spacer />
      <Flex
        gap="2"
        position="sticky"
        bottom="0"
        bg="gray.50"
        zIndex="99"
        padding="2"
        borderTop={hasOverflow ? "1px solid" : "unset"}
        borderColor="gray.300"
      >
        <Button
          variant="outline"
          colorScheme="gray"
          width="50%"
          isLoading={isResetting}
          onClick={() => {
            setIsResetting(true);
            dispatch(clearFilters());
            dispatch(applyFilters());
            setTimeout(() => {
              setIsResetting(false);
              onClose();
            }, 1);
          }}
        >
          Clear all
        </Button>
        <Button
          colorScheme="primary"
          width="50%"
          isLoading={isLoading}
          onClick={() => {
            setIsLoading(true);
            dispatch(applyFilters());
            setTimeout(() => {
              setIsLoading(false);
              onClose();
            }, 1);
          }}
        >
          Filter
        </Button>
      </Flex>
    </Flex>
  );
};

function useHasOverflow<T extends HTMLElement>(): [
  (node: T | null) => void,
  boolean
] {
  const ref = React.useRef<T | null>(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const observerRef = React.useRef<ResizeObserver | null>(null);

  const setRef = React.useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    ref.current = node;
    if (!node) return;

    const check = () => setHasOverflow(node.scrollHeight > node.clientHeight);

    const observer = new ResizeObserver(check);
    observer.observe(node);
    for (const child of Array.from(node.children)) {
      observer.observe(child);
    }
    observerRef.current = observer;
    check();
  }, []);

  return [setRef, hasOverflow];
}
